// cleanup_convert_to_glu_component_LOGIN_WORKFLOW
// cleanup_hoc_to_hooks_LOGIN_WORKFLOW
// cleanup_convert_to_typescript_file_LOGIN_WORKFLOW
// cleanup_class_to_function_LOGIN_WORKFLOW

import React, { Component } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import jsCookie from 'js-cookie';
import { of } from 'rxjs';
import { mergeMap, map } from 'rxjs/operators';

import Alert from '@glu/alerts-react';
import { withStyles } from '@glu/theming';

import { STORAGE_KEYS, ALERTVARIATIONS } from 'constants.js';
import {
  generateSecurityKey,
  rememberDevice,
  validateSecurityKey
} from 'api/deviceChallenge/service';
import withLocale from 'components/withLocale/withLocale';
import SessionExpiration from 'components/sessionExpiration/SessionExpiration';
import { unsubscribeComponent } from 'utils/unsubscribeComponent';

import styles from './DeviceChallenge.styles';

export class DeviceChallengeComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      deviceKey: '',
      errorMessage: '',
      rememberDevice: false,
      sending: false,
      successMessage: '',
      token: jsCookie.get(STORAGE_KEYS.TOKEN)
    };
  }

  componentDidMount() {
    /** @description clear the token, so they will be logged out on refresh. */
    jsCookie.remove(STORAGE_KEYS.TOKEN);
    return this.sendCode();
  }

  componentWillUnmount() {
    unsubscribeComponent(this.subscriptions);
  }

  observables = {
    generateSecurityKey$: null,
    validateSecurityKey$: null
  };

  subscriptions = {
    generateSecurityKey: null,
    validateSecurityKey: null
  };

  clear = (item) => () => this.setState({ [item]: '' });

  handleInputChange = (event) => {
    const { target } = event;
    const { checked, name, type, value } = target;

    if (type === 'checkbox') {
      return this.setState({ [name]: checked });
    }

    const validValue = value.replace(/\s/g, '');
    return this.setState({ [name]: validValue });
  };

  sendCode = () => {
    const first = !this.observables.generateSecurityKey$;

    this.setState({ sending: true });
    this.observables.generateSecurityKey$ = generateSecurityKey();
    this.subscriptions.generateSecurityKey = this.observables.generateSecurityKey$
      .pipe(map((data) => ({ ...data, first })))
      .subscribe(this.codeSent, this.handleError);

    return this.observables.generateSecurityKey$;
  };

  codeSent = (data) => {
    const { locale } = this.props;
    if (!data.success) {
      return this.handleError(data);
    }

    if (data.first) {
      this.setState({ sending: false });
      return null;
    }

    // TODO need copy on success message
    return this.setState({
      errorMessage: '',
      sending: false,
      successMessage: locale['deviceChallenge.keySent']
    });
  };

  verifyCode = (event) => {
    event.preventDefault();
    this.setState({ sending: true });
    this.observables.validateSecurityKey$ = validateSecurityKey(this.state.deviceKey).pipe(
      mergeMap(this.detectRemember)
    );

    this.subscriptions.validateSecurityKey = this.observables.validateSecurityKey$.subscribe(
      this.verified,
      this.handleError
    );

    return false;
  };

  detectRemember = (data) => {
    if (!data.success) {
      return of(data);
    }

    if (!this.state.rememberDevice) {
      return of(data);
    }
    return rememberDevice().pipe(map(() => data));
  };

  handleError = ({ data }) => {
    const { locale } = this.props;
    const { code, userMessage } = data?.[0] || {};

    this.setState({
      errorMessage: locale[code] || userMessage || locale['common.server.error'],
      sending: false,
      deviceKey: '',
      successMessage: ''
    });
  };

  verified = (data) => {
    if (!data.success) {
      return this.handleError(data);
    }

    jsCookie.set(STORAGE_KEYS.TOKEN, this.state.token);
    this.setState({ sending: false });

    /** @description
     * We are overriding `deviceInfoAvailable`,
     * because we need to optionally remember the device. */
    return this.props.onSuccess({
      ...this.props.user,
      deviceInfoAvailable: true,
      deviceInfoPassed: true
    });
  };

  render() {
    const {
      classes: { buttonLink, checkLabel },
      locale
    } = this.props;

    const { deviceKey, errorMessage, rememberDevice, sending, successMessage, token } = this.state;

    return (
      <form onSubmit={this.verifyCode}>
        <input type="hidden" id="csrf" name="csrf" value={token} />
        {errorMessage && (
          <Alert
            dataQa="deviceChallengeError"
            variant={ALERTVARIATIONS.ERROR}
            onHide={this.clear('errorMessage')}
            message={errorMessage}
          />
        )}

        {successMessage && (
          <Alert
            dataQa="deviceChallengeSuccess"
            variant={ALERTVARIATIONS.SUCCESS}
            onHide={this.clear('successMessage')}
            suppressIcon
            message={successMessage}
          />
        )}
        <div className="form-group">
          <b>{locale['deviceChallenge.header1']}</b>
        </div>
        <p>{locale['deviceChallenge.header2']}</p>

        <div className="form-group">
          <label htmlFor="deviceKey">{locale['deviceChallenge.enterKey']}</label>
          <input
            className="form-control"
            disabled={sending}
            id="deviceKey"
            name="deviceKey"
            onChange={this.handleInputChange}
            value={deviceKey}
            data-qa="deviceKey"
          />
          <label htmlFor="rememberDevice" className={checkLabel}>
            <input
              disabled={sending}
              id="rememberDevice"
              name="rememberDevice"
              onChange={this.handleInputChange}
              value={rememberDevice}
              type="checkbox"
            />
            {locale['deviceChallenge.rememberDevice']}
          </label>
        </div>

        <div className="form-group">
          <button
            data-qa="submit-key"
            className="btn btn-primary"
            type="submit"
            disabled={sending || !deviceKey}
          >
            {locale['common.submit']}
          </button>
          <button className="btn btn-link" disabled={sending} onClick={this.props.onCancel}>
            {locale['common.cancel']}
          </button>
        </div>

        <hr />

        <button
          className={classnames('btn btn-link', buttonLink)}
          disabled={sending}
          onClick={this.sendCode}
          type="button"
          data-qa="send-new-key"
        >
          {locale['deviceChallenge.sendKey']}
        </button>

        <SessionExpiration onLogout={this.props.onCancel} />
      </form>
    );
  }
}

const DeviceChallengeStyled = withStyles(styles)(DeviceChallengeComponent);
export default withLocale(DeviceChallengeStyled);

DeviceChallengeComponent.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func,
  locale: PropTypes.objectOf(PropTypes.string),
  user: PropTypes.shape({})
};

DeviceChallengeComponent.defaultProps = {
  classes: {},
  locale: {},
  onCancel() {},
  onSuccess() {},
  user: null
};
